import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Copy, Eye, Lock, Mail, MessageCircle, Pencil, Plus, Star } from 'lucide-react';
import {
  archiveReminderTemplate,
  duplicateSystemReminderTemplate,
  updateCollectionSettings,
} from '../../../../api/collections';
import { showToast } from '../../../../utils/showToast';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Modal } from '../../../../components/ui/Modal';
import { Skeleton } from '../../../../components/ui/Skeleton';
import ReminderMessagePreview from '../components/ReminderMessagePreview';
import ReminderTemplateEditor from './ReminderTemplateEditor';
import {
  CHANNEL_LABELS,
  REMINDER_CHANNELS,
  apiErrorCode,
  apiErrorStatus,
  formatInstant,
  isRequestOutcomeUnknown,
  newIdempotencyKey,
} from '../utils/collectionReminders';
import {
  copyTemplateName,
  draftFromTemplate,
  isChannelAtLimit,
  templateErrorText,
  templatesOfChannel,
  usedInSteps,
} from '../utils/reminderTemplates';

const CHANNEL_ICONS = { email: Mail, whatsapp: MessageCircle };

/** Primera línea con texto del cuerpo (resumen de la tarjeta). Texto del operador: sin t(). */
const firstLine = (body) =>
  String(body ?? '')
    .split('\n')
    .map((line) => line.trim())
    .find(Boolean) ?? '';

/** Dónde se usa una plantilla, según billing (`usedIn`). */
function UsageText({ usedIn }) {
  const { t } = useTranslation();
  const steps = usedInSteps(usedIn);
  if (steps.length > 0) {
    return <span>{t('Usada en los pasos de {{dias}} días de atraso', { dias: steps.join(', ') })}</span>;
  }
  if (usedIn?.default) return null;
  return <span>{t('No se usa en la configuración')}</span>;
}

/** Explicación del 409 template_in_use: en qué lugares de la configuración está. */
function InUseDetail({ usedIn, channel, onGoToSettings }) {
  const { t } = useTranslation();
  const steps = usedInSteps(usedIn);
  return (
    <div className="space-y-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning" role="alert">
      <p className="m-0">{t('No se puede archivar: la configuración de cobranza la usa.')}</p>
      <ul className="m-0 list-disc space-y-0.5 pl-5">
        {usedIn?.default && (
          <li>{t('Es la plantilla por defecto de {{canal}}.', { canal: t(CHANNEL_LABELS[channel]) })}</li>
        )}
        {steps.length > 0 && (
          <li>{t('Está asignada a los pasos de {{dias}} días de atraso.', { dias: steps.join(', ') })}</li>
        )}
      </ul>
      <p className="m-0">{t('Elegí otra plantilla en esos lugares y volvé a intentar.')}</p>
      <Button size="sm" variant="outline" onClick={onGoToSettings}>
        {t('Ir a Configuración')}
      </Button>
    </div>
  );
}

/**
 * Plantillas de los recordatorios (/admin/cobro/cobranza?tab=plantillas): por canal, la del
 * sistema (solo lectura, se duplica para editar) y las del operador, con dónde se usa cada
 * una y las acciones Editar, Duplicar, Marcar por defecto y Archivar.
 *
 * "Marcar por defecto" guarda la configuración (PUT settings con solo `defaultTemplateIds`):
 * por eso avisa a la pantalla para recargarla. Archivar no borra: los envíos ya programados
 * salen con la versión que tenían; billing responde 409 si la configuración la usa.
 *
 * @param {{ templates: ReturnType<typeof import('../hooks/useReminderTemplates').default>,
 *   timeZone?: string, onSettingsChanged?: () => Promise<void>|void,
 *   onGoToSettings?: () => void }} props
 */
export default function ReminderTemplatesTab({ templates, timeZone, onSettingsChanged, onGoToSettings }) {
  const { t } = useTranslation();
  const { catalog, loading, error, reload } = templates;
  const [editor, setEditor] = useState(null); // { key, mode, channel, template?, initialDraft? }
  const [busy, setBusy] = useState(null);
  const [systemPreview, setSystemPreview] = useState(null); // canal
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveInUse, setArchiveInUse] = useState(null); // usedIn del 409
  const [archiving, setArchiving] = useState(false);
  // Clave de "Duplicar para editar" por canal: se conserva si no se sabe si billing creó la
  // copia, para que el reintento devuelva esa misma copia en vez de crear otra.
  const duplicateKeys = useRef({});
  const editorSeq = useRef(0);

  const openEditor = (params) => {
    editorSeq.current += 1;
    setEditor({ key: editorSeq.current, ...params });
  };

  const reloadTemplate = async (templateId) => {
    const fresh = await reload();
    if (!fresh) return undefined;
    return (fresh.items ?? []).find((item) => item.id === templateId) ?? null;
  };

  const handleSaved = async (_saved, mode) => {
    setEditor(null);
    showToast(mode === 'create' ? t('Plantilla creada.') : t('Plantilla guardada.'), 'success');
    await reload();
  };

  // `templateId` null = volver a la plantilla del sistema.
  const markDefault = async (channel, templateId) => {
    setBusy(`default:${templateId ?? `system-${channel}`}`);
    try {
      await updateCollectionSettings({ defaultTemplateIds: { [channel]: templateId } });
      showToast(t('Plantilla por defecto actualizada.'), 'success');
    } catch (err) {
      showToast(templateErrorText(err, t, 'No se pudo cambiar la plantilla por defecto.'), 'error');
    } finally {
      setBusy(null);
      // Tras un error también: la plantilla pudo archivarse o la configuración cambiar.
      await Promise.all([reload(), onSettingsChanged?.()]);
    }
  };

  const duplicateSystem = async (channel) => {
    let key = duplicateKeys.current[channel];
    if (!key) {
      try {
        key = newIdempotencyKey();
      } catch {
        showToast(t('Este navegador no permite generar una clave segura. Actualizalo o probá con otro.'), 'error');
        return;
      }
      duplicateKeys.current[channel] = key;
    }
    setBusy(`duplicate:${channel}`);
    try {
      const { data } = await duplicateSystemReminderTemplate({ channel, idempotencyKey: key });
      delete duplicateKeys.current[channel];
      await reload();
      openEditor({ mode: 'edit', channel, template: data });
    } catch (err) {
      if (!isRequestOutcomeUnknown(err)) delete duplicateKeys.current[channel];
      showToast(templateErrorText(err, t, 'No se pudo duplicar la plantilla del sistema.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const closeArchive = () => {
    if (archiving) return;
    setArchiveTarget(null);
    setArchiveInUse(null);
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await archiveReminderTemplate(archiveTarget.id);
      showToast(t('Plantilla archivada.'), 'success');
      setArchiveTarget(null);
    } catch (err) {
      if (apiErrorCode(err) === 'template_in_use') {
        setArchiveInUse(err.response.data.usedIn ?? {});
      } else {
        showToast(
          apiErrorStatus(err) === 404
            ? t('La plantilla ya no existe o fue archivada.')
            : templateErrorText(err, t, 'No se pudo archivar la plantilla.'),
          'error',
        );
        setArchiveTarget(null);
      }
    } finally {
      setArchiving(false);
      await reload();
    }
  };

  if (loading && !catalog) {
    return (
      <div className="space-y-4" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }
  if (!catalog) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
        <span>{error?.message || t('No se pudieron cargar las plantillas.')}</span>
        <Button size="sm" variant="outline" onClick={reload}>
          {t('Reintentar')}
        </Button>
      </div>
    );
  }

  const max = catalog.limits?.maxActiveTemplatesPerChannel;

  const renderSystem = (channel) => {
    const system = catalog.system?.[channel];
    if (!system) return null;
    return (
      <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 sm:px-5">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-fg">{t('Plantilla del sistema')}</span>
            {system.isDefault && <Badge tone="brand">{t('Por defecto')}</Badge>}
            <Badge tone="muted">
              <Lock className="h-3 w-3" aria-hidden />
              {t('Solo lectura')}
            </Badge>
          </div>
          <p className="m-0 text-xs text-fg-muted">
            {t('El texto estándar de Beartrack. No se puede editar: duplicala para crear una propia.')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" iconLeft={<Eye className="h-4 w-4" />} onClick={() => setSystemPreview(channel)}>
            {t('Ver mensaje')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            iconLeft={<Copy className="h-4 w-4" />}
            loading={busy === `duplicate:${channel}`}
            disabled={!!busy || isChannelAtLimit(catalog, channel)}
            onClick={() => duplicateSystem(channel)}
          >
            {t('Duplicar para editar')}
          </Button>
          {!system.isDefault && (
            <Button
              size="sm"
              variant="ghost"
              iconLeft={<Star className="h-4 w-4" />}
              loading={busy === `default:system-${channel}`}
              disabled={!!busy}
              onClick={() => markDefault(channel, null)}
            >
              {t('Marcar por defecto')}
            </Button>
          )}
        </div>
      </li>
    );
  };

  const renderTemplate = (channel, template) => {
    const summary = channel === 'email' ? template.subject : firstLine(template.body);
    const updated = formatInstant(template.updatedAt, timeZone);
    return (
      <li key={template.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 sm:px-5">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {/* Nombre y texto del operador: van tal cual, nunca por t(). */}
            <span className="break-words font-medium text-fg">{template.name}</span>
            {template.usedIn?.default && <Badge tone="brand">{t('Por defecto')}</Badge>}
          </div>
          {summary && <p className="m-0 truncate text-sm text-fg-muted">{summary}</p>}
          <p className="m-0 flex flex-wrap gap-x-2 text-xs text-fg-muted">
            <UsageText usedIn={template.usedIn} />
            <span>
              {t('Versión {{n}}', { n: template.version })}
              {updated ? ` · ${t('Actualizada el {{fecha}}', { fecha: updated })}` : ''}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            iconLeft={<Pencil className="h-4 w-4" />}
            disabled={!!busy}
            onClick={() => openEditor({ mode: 'edit', channel, template })}
          >
            {t('Editar')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<Copy className="h-4 w-4" />}
            disabled={!!busy || isChannelAtLimit(catalog, channel)}
            onClick={() =>
              openEditor({
                mode: 'create',
                channel,
                initialDraft: {
                  ...draftFromTemplate(template),
                  name: copyTemplateName(t('Copia de'), template.name, catalog.limits?.nameMaxLength),
                },
              })
            }
          >
            {t('Duplicar')}
          </Button>
          {!template.usedIn?.default && (
            <Button
              size="sm"
              variant="ghost"
              iconLeft={<Star className="h-4 w-4" />}
              loading={busy === `default:${template.id}`}
              disabled={!!busy}
              onClick={() => markDefault(channel, template.id)}
            >
              {t('Marcar por defecto')}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<Archive className="h-4 w-4" />}
            disabled={!!busy}
            onClick={() => setArchiveTarget(template)}
          >
            {t('Archivar')}
          </Button>
        </div>
      </li>
    );
  };

  const systemSample = systemPreview ? catalog.system?.[systemPreview]?.sample ?? {} : null;

  return (
    <div className="space-y-4">
      <p className="m-0 text-sm text-fg-muted">
        {t('Los recordatorios salen con la plantilla del paso o, si no tiene, con la plantilla por defecto del canal. Se asignan en Configuración.')}
      </p>

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning" role="alert">
          <span>{t('No se pudo actualizar la lista de plantillas. Se muestra la última que se cargó.')}</span>
          <Button size="sm" variant="outline" onClick={reload} disabled={loading}>
            {t('Reintentar')}
          </Button>
        </div>
      )}

      {REMINDER_CHANNELS.map((channel) => {
        const Icon = CHANNEL_ICONS[channel];
        const own = templatesOfChannel(catalog, channel);
        const atLimit = isChannelAtLimit(catalog, channel);
        return (
          <section key={channel} className="bill-card bill-card--clip" aria-labelledby={`tpl-${channel}-title`}>
            <div className="bill-card__head flex-wrap">
              <h3 id={`tpl-${channel}-title`} className="bill-card__title flex items-center gap-2">
                <Icon className="h-4 w-4 text-fg-muted" aria-hidden />
                {t(CHANNEL_LABELS[channel])}
                {Number.isInteger(max) && <span>{t('{{n}} de {{max}} plantillas', { n: own.length, max })}</span>}
              </h3>
              <Button
                size="sm"
                iconLeft={<Plus className="h-4 w-4" />}
                disabled={atLimit}
                onClick={() => openEditor({ mode: 'create', channel })}
              >
                {t('Nueva plantilla')}
              </Button>
            </div>
            {atLimit && (
              <p className="m-0 px-4 pt-3 text-xs text-status-warning sm:px-5">
                {t('Llegaste al máximo de {{max}} plantillas activas para este canal. Archivá alguna para crear otra.', { max })}
              </p>
            )}
            <ul className="m-0 list-none divide-y divide-border p-0">
              {renderSystem(channel)}
              {own.map((template) => renderTemplate(channel, template))}
            </ul>
            {own.length === 0 && (
              <p className="m-0 border-t border-border px-4 py-3 text-sm text-fg-muted sm:px-5">
                {t('Todavía no hay plantillas propias. Duplicá la del sistema para empezar desde su texto.')}
              </p>
            )}
          </section>
        );
      })}

      {editor && (
        <ReminderTemplateEditor
          key={editor.key}
          mode={editor.mode}
          channel={editor.channel}
          template={editor.template}
          initialDraft={editor.initialDraft}
          catalog={catalog}
          onClose={() => setEditor(null)}
          onSaved={handleSaved}
          onReloadTemplate={reloadTemplate}
        />
      )}

      <Modal
        open={!!systemPreview}
        onClose={() => setSystemPreview(null)}
        size="lg"
        title={t('Plantilla del sistema')}
        description={systemPreview ? `${t(CHANNEL_LABELS[systemPreview])} · ${t('Con datos de ejemplo.')}` : undefined}
      >
        {systemPreview && (
          <ReminderMessagePreview
            channel={systemPreview}
            subject={systemSample.subject}
            html={systemSample.html}
            text={systemSample.text}
          />
        )}
      </Modal>

      <Modal
        open={!!archiveTarget}
        onClose={closeArchive}
        size="md"
        title={t('Archivar plantilla')}
        footer={
          <>
            <Button variant="outline" onClick={closeArchive} disabled={archiving}>
              {t('Cancelar')}
            </Button>
            {!archiveInUse && (
              <Button variant="danger" onClick={handleArchive} loading={archiving}>
                {t('Archivar')}
              </Button>
            )}
          </>
        }
      >
        {archiveTarget && (
          <div className="space-y-3 text-sm text-fg">
            <p className="m-0 font-medium break-words">{archiveTarget.name}</p>
            <p className="m-0">
              {t('Deja de estar disponible para elegir en la configuración y en los envíos. Los envíos ya programados con ella salen igual, con el texto que tenían.')}
            </p>
            {archiveInUse && (
              <InUseDetail
                usedIn={archiveInUse}
                channel={archiveTarget.channel}
                onGoToSettings={() => {
                  closeArchive();
                  onGoToSettings?.();
                }}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
